// bb-countdown.js
// Boosted Brat — drop countdown, custom-element logic.
//
// WHY A CUSTOM ELEMENT IN AN EXTERNAL MODULE
// ------------------------------------------
// This block used to carry its logic in an inline IIFE <script> tag. That runs
// on a full page load, but NOT when Shopify's Section Rendering API re-inserts
// the block — which is what the theme editor does on every setting change. The
// browser does not execute <script> nodes inserted via innerHTML, so in the
// editor the countdown just sat there, dashes and all, until a hard reload.
//
// A custom element sidesteps that: whenever the browser parses a <bb-countdown>
// tag — first paint OR an SRA re-render — it upgrades the element and calls
// connectedCallback automatically. No inline script to skip. The markup carries
// every merchant setting as data-attributes (this file is not Liquid-evaluated,
// so it cannot read block.settings directly), and connectedCallback reads them
// off `this`. See the header comment in bb-countdown.liquid for the phase model,
// timezone handling, and accessibility rationale — all preserved here verbatim.

class BBCountdown extends HTMLElement {
  connectedCallback() {
    // Double-init guard: a flag on the element itself. A fresh SRA render makes
    // a brand new element (fresh flag), so it re-inits as intended; the guard
    // only stops a re-entrant connect on the same node.
    if (this._bbInit) return;
    this._bbInit = true;

    var root = this;

    var MODE     = root.dataset.mode || 'auto';
    var TZ       = root.dataset.tz || 'America/Chicago';
    var OPEN_S   = (root.dataset.open || '').trim();
    var CLOSE_S  = (root.dataset.close || '').trim();
    var URGENT_MS = (parseFloat(root.dataset.urgencyHours) || 0) * 3600000;
    var OVERLAP_MS = (parseFloat(root.dataset.overlapDays) || 0) * 86400000;
    var SECONDS_MODE = root.dataset.seconds || 'auto';

    // Config that the inline script used to interpolate via Liquid now arrives
    // as data-attributes on the element (this module is not Liquid-evaluated).
    var DROP     = root.dataset.dropLabel || '';
    var L_OPENS  = root.dataset.opensLabel || '';
    var L_CLOSES = root.dataset.closesLabel || '';
    var L_DAY    = root.dataset.labelDays || '';
    var L_HOUR   = root.dataset.labelHours || '';
    var L_MIN    = root.dataset.labelMinutes || '';
    var L_SEC    = root.dataset.labelSeconds || '';
    var EXPIRED  = root.dataset.expiredMessage || '';

    var phaseEl   = root.querySelector('[data-bb-phase-label]');
    var unitsEl   = root.querySelector('[data-bb-units]');
    var expiredEl = root.querySelector('[data-bb-expired]');
    var overlapEl = root.querySelector('[data-bb-overlap]');
    var summaryEl = root.querySelector('[data-bb-summary]');
    var announceEl = root.querySelector('[data-bb-announce]');

    var cells = {
      days:    root.querySelector('[data-bb-num="days"]'),
      hours:   root.querySelector('[data-bb-num="hours"]'),
      minutes: root.querySelector('[data-bb-num="minutes"]'),
      seconds: root.querySelector('[data-bb-num="seconds"]')
    };

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ---- timezone resolution -------------------------------------------------
    // Read the wall clock that `ms` shows in `tz`, expressed as a UTC timestamp.
    // The difference between that and `ms` is the zone's offset at that instant.
    function wallClockOf(ms, tz) {
      var dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      var p = {};
      var parts = dtf.formatToParts(new Date(ms));
      for (var i = 0; i < parts.length; i++) p[parts[i].type] = parts[i].value;
      var h = parseInt(p.hour, 10);
      if (h === 24) h = 0; // some engines report midnight as hour 24
      return Date.UTC(+p.year, +p.month - 1, +p.day, h, +p.minute, +p.second);
    }

    // Convert a merchant wall clock in `tz` to one absolute UTC instant.
    // Fixed-point iteration: we want T where wallClockOf(T) === naive, i.e.
    // T = naive - offset(T). Two passes converge, including across a DST
    // boundary where the offset on the target date differs from today's.
    function zonedToUTC(iso, tz) {
      var m = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})/.exec(iso || '');
      if (!m) return null;
      var naive = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0);
      if (!isFinite(naive)) return null;
      try {
        var guess = naive;
        for (var i = 0; i < 2; i++) {
          guess = naive - (wallClockOf(guess, tz) - guess);
        }
        return guess;
      } catch (e) {
        // Intl or the zone name is unavailable. Treat the wall clock as UTC
        // rather than as visitor-local: a fixed, stated fallback beats a value
        // that is wrong by a different amount for every visitor. The printed
        // absolute time below remains correct regardless.
        return naive;
      }
    }

    var openMs  = OPEN_S ? zonedToUTC(OPEN_S, TZ) : null;
    var closeMs = CLOSE_S ? zonedToUTC(CLOSE_S, TZ) : null;

    // ---- phase machine -------------------------------------------------------
    // opens   -> counting down to open_at
    // closes  -> counting down to close_at
    // expired -> past close_at, or nothing left to count
    function phaseAt(now) {
      if (MODE === 'closes') {
        if (closeMs && now < closeMs) return { phase: 'closes', target: closeMs };
        return { phase: 'expired', target: null };
      }
      if (MODE === 'opens') {
        if (openMs && now < openMs) return { phase: 'opens', target: openMs };
        // Already open. Roll into the close if one is set rather than going
        // dark — during the overlap this is the block that just went live.
        if (closeMs && now < closeMs) return { phase: 'closes', target: closeMs };
        return { phase: 'expired', target: null };
      }
      // auto
      if (openMs && now < openMs) return { phase: 'opens', target: openMs };
      if (closeMs && now < closeMs) return { phase: 'closes', target: closeMs };
      return { phase: 'expired', target: null };
    }

    function breakdown(ms) {
      if (ms < 0) ms = 0;
      var total = Math.floor(ms / 1000);
      return {
        days: Math.floor(total / 86400),
        hours: Math.floor(total / 3600) % 24,
        minutes: Math.floor(total / 60) % 60,
        seconds: total % 60
      };
    }

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function setCell(el, text) {
      if (!el || el.textContent === text) return;
      el.textContent = text;
      if (reduced) return;
      // Alternate the pulse attribute so the animation-name changes and the
      // animation restarts. No forced reflow. See the CSS note above.
      el.setAttribute('data-bb-pulse', el.getAttribute('data-bb-pulse') === 'a' ? 'b' : 'a');
    }

    // Readable sentence for assistive tech. Never announced — see header note.
    function summarise(phase, b, secondsOn) {
      if (phase === 'expired') return (DROP ? DROP + '. ' : '') + EXPIRED;
      var verb = phase === 'opens' ? L_OPENS : L_CLOSES;
      var bits = [];
      if (b.days) bits.push(b.days + ' ' + L_DAY.toLowerCase());
      if (b.days || b.hours) bits.push(b.hours + ' ' + L_HOUR.toLowerCase());
      bits.push(b.minutes + ' ' + L_MIN.toLowerCase());
      if (secondsOn && !b.days) bits.push(b.seconds + ' ' + L_SEC.toLowerCase());
      return (DROP ? DROP + ' ' : '') + verb.toLowerCase() + ' ' + bits.join(', ') + '.';
    }

    var lastPhase = null;
    var lastSummaryKey = null;

    function tick() {
      var now = Date.now();
      var state = phaseAt(now);
      var phase = state.phase;

      if (phase === 'expired') {
        root.dataset.bbPhase = 'expired';
        root.dataset.bbUrgent = 'false';
        if (expiredEl) expiredEl.hidden = false;
        if (overlapEl) overlapEl.hidden = true;
        if (summaryEl) summaryEl.textContent = summarise('expired');
        if (phase !== lastPhase) {
          if (announceEl && lastPhase !== null) announceEl.textContent = EXPIRED;
          lastPhase = phase;
        }
        stop();
        return;
      }

      var remaining = state.target - now;
      var secondsOn = SECONDS_MODE === 'always' ||
                      (SECONDS_MODE === 'auto' && remaining < 86400000);

      var b = breakdown(remaining);

      root.dataset.bbPhase = phase;
      root.dataset.bbSeconds = secondsOn ? 'on' : 'off';
      root.dataset.bbUrgent = (URGENT_MS > 0 && remaining <= URGENT_MS) ? 'true' : 'false';

      if (expiredEl) expiredEl.hidden = true;
      if (phaseEl) phaseEl.textContent = phase === 'opens' ? L_OPENS : L_CLOSES;

      setCell(cells.days, pad(b.days));
      setCell(cells.hours, pad(b.hours));
      setCell(cells.minutes, pad(b.minutes));
      if (secondsOn) setCell(cells.seconds, pad(b.seconds));

      // Overlap notice: the closing drop points at the one already open.
      if (overlapEl) {
        overlapEl.hidden = !(phase === 'closes' && OVERLAP_MS > 0 && remaining <= OVERLAP_MS);
      }

      // Rebuild the summary only when the displayed minute actually moves.
      var key = phase + '|' + b.days + '|' + b.hours + '|' + b.minutes;
      if (key !== lastSummaryKey) {
        lastSummaryKey = key;
        if (summaryEl) summaryEl.textContent = summarise(phase, b, secondsOn);
      }

      // One announcement per phase transition. Not per tick.
      if (phase !== lastPhase) {
        if (announceEl && lastPhase !== null) {
          announceEl.textContent = summarise(phase, b, secondsOn);
        }
        lastPhase = phase;
      }

      schedule(secondsOn);
    }

    // Align to the next boundary instead of setInterval, which drifts and
    // fires while the tab is asleep. Minute cadence until the last day keeps
    // a 30 day countdown from running a 1Hz timer for a month.
    function schedule(secondsOn) {
      stop();
      var step = secondsOn ? 1000 : 60000;
      var delay = step - (Date.now() % step);
      if (delay < 50) delay += step;
      root._timer = setTimeout(tick, delay);
    }

    function stop() {
      if (root._timer) { clearTimeout(root._timer); root._timer = null; }
    }

    // Keep a handle on the visibility listener so disconnectedCallback can
    // remove it — an SRA re-render discards the old element and a leaked
    // document-level listener would tick a detached node forever.
    root._onVisibility = function () {
      if (document.hidden) {
        stop();
      } else if (root.dataset.bbPhase !== 'expired') {
        tick();
      }
    };
    document.addEventListener('visibilitychange', root._onVisibility);

    if (!openMs && !closeMs) {
      // Nothing configured. Show the expired copy rather than a row of dashes.
      root.dataset.bbPhase = 'expired';
      if (expiredEl) expiredEl.hidden = false;
      if (summaryEl) summaryEl.textContent = summarise('expired');
    } else {
      tick();
    }
  }

  disconnectedCallback() {
    // Clear the pending timeout and drop the visibilitychange listener added in
    // connectedCallback. Without this, a detached element keeps a document-level
    // listener alive and its timer would keep firing against nodes no longer in
    // the DOM — a leak the theme editor would hit on every re-render.
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    if (this._onVisibility) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
  }
}

if (!customElements.get('bb-countdown')) customElements.define('bb-countdown', BBCountdown);
